import React, { useRef } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import { PAYSEND_PLUS } from 'apps/auth/components/paysendPlus/utils';
import TextPopup from 'apps/auth/components/sharedComponents/popups/TextPopup';
import { transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import { FEE_INFO_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_COUNTRY, PAYMENT_CURRENCY, PAYOUT_COUNTRY, PAYOUT_CURRENCY, PAYOUT_METHOD, SCREEN } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { CALCULATOR } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { PROMO_CODE_FIELD } from 'apps/auth/constants/fieldsName';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useLanguage } from 'apps/auth/hooks/selectors';

import { ListItemTitle } from './styles';
import FeeTextPopup from '../../send/popups/FeeTextPopup';

const FeeInfoPureComponent = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const popupRef = useRef(null);
    let { deliveryMethod, fromCountry, toCountry, toCurrency, fromCurrency, sendFlowV3, dcSources, availablePaymentMethodsList, calculatorFeePopup } =
        useExtra();

    const canUseSaveMoneyCode = props.canUsePromoCode;
    const showSaveMoneyInfo = canUseSaveMoneyCode && props.promoCode.value;
    const textFeePopup = props.calculatorFeePopup || props.commissionInfo?.tariffInfo;
    const showFeePopup = textFeePopup || showSaveMoneyInfo;
    const hasPromoForFirstPayment = calculatorFeePopup && fromCountry.promoForPayment?.type;
    const withIncluded = sendFlowV3 && !hasPromoForFirstPayment;

    const getText = () => props.promoCodeApplied;
    const getValue = () => props.promoCode.value;

    const canUsePrefix = (availablePaymentMethodsList = {}, dcSources = {}, isBonus = false) => {
        let canUsePrefix = true;

        if (!sendFlowV3 || hasPromoForFirstPayment || props.commissionInfo?.bonusSpecialRate) {
            return false;
        }

        if (isBonus) {
            return false;
        }

        Object.values(dcSources).forEach((v) => {
            //TODO add hasAmountRestriction some how
            if (Array.isArray(v) && v.length > 0) {
                Object.values(v).forEach((p) => {
                    if (p && p.lastSuccess) {
                        canUsePrefix = false;
                    }
                });
            }
        });

        Object.values(availablePaymentMethodsList).forEach((method) => {
            if (method && method.lastSuccess) {
                canUsePrefix = false;
            }
        });
        return canUsePrefix;
    };

    const getCurrentFee = (fee = '', availablePaymentMethodsList, isBonus) => {
        if (canUsePrefix(availablePaymentMethodsList, dcSources, isBonus)) {
            return `${resolveArguments(props.fromFeePrefix, fee)}`;
        }

        return fee;
    };

    const getFeeColorText = (commissionInfo) => {
        if (commissionInfo?.fee === 0 || props.commissionInfo?.bonusSpecialCommission) {
            return 'purple';
        }
        return commissionInfo?.subscriptionType === PAYSEND_PLUS ? 'emphasized' : 'primary';
    };

    const openFeePopup = (e) => {
        e.preventDefault();
        amplitude.getInstance().logEvent(FEE_INFO_CLICKED, {
            [SCREEN]: CALCULATOR,
            [PAYOUT_METHOD]: transactionMethods[deliveryMethod],
            [PAYOUT_CURRENCY]: toCurrency?.code,
            [PAYMENT_CURRENCY]: fromCurrency?.code,
            [PAYOUT_COUNTRY]: toCountry?.nameEn,
            [PAYMENT_COUNTRY]: fromCountry?.nameEn,
        });
        if (showSaveMoneyInfo) {
            popupRef.current?.open(<TextPopup text={resolveArguments(getText(), getValue())} buttonText={props.gotIt} popupRef={popupRef} />);
        } else {
            popupRef.current?.open(
                <FeeTextPopup
                    text={textFeePopup}
                    countryToId={null}
                    deliveryMethod={null}
                    isComment={true}
                    isShowTerms={false}
                    popupRef={popupRef}
                    subscriptionType={props.commissionInfo?.subscriptionType}
                />,
            );
        }
    };

    return (
        <>
            <ListItemTitle showInfo={showFeePopup} onClick={showFeePopup ? openFeePopup : null}>
                <Text size="secondary" color="secondary" variant="div" indent="4px">
                    {withIncluded ? props.feeIncludedTitle : props.feeTitle}
                </Text>
                {showFeePopup && <DiskButton size="small" iconName="info-rounded" iconColor="secondary" type="button" raised={false} />}
            </ListItemTitle>
            <Text size="secondary" color={getFeeColorText(props.commissionInfo)} variant="div">
                {`${getCurrentFee(getLocalizedRawAmount(lang, country, props.feeAmount), availablePaymentMethodsList, props.isBonus)}  `}
                <Text size="secondary" color={getFeeColorText(props.commissionInfo)} smallCaps>
                    {' '}
                    {props.fromCurrencyIso}
                </Text>
            </Text>
            <PopupNew ref={popupRef} />
        </>
    );
};

FeeInfoPureComponent.propTypes = {
    promoCode: PropTypes.object,
    formName: PropTypes.string.isRequired,
    fromCurrencyIso: PropTypes.string,
    feeAmount: PropTypes.number,
    canUsePromoCode: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
    return {
        feeTitle: state.messages.pageMessages.fee,
        feeIncludedTitle: state.messages.pageMessages.feeIncluded,
        fromFeePrefix: state.messages.pageMessages.fromFeePrefix,
        promoCode: state.form[ownProps.formName].fields[PROMO_CODE_FIELD],
        gotIt: state.messages.pageMessages.gotIt,
        promoCodeApplied: state.messages.pageMessages.promoCodeApplied,
        feeAmount: state.extra.commission?.fee,
        canUsePromoCode: state.extra.canUsePromoCode,
        calculatorFeePopup: state.extra.calculatorFeePopup,
    };
};

const FeeInfo = connect(mapStateToProps)(FeeInfoPureComponent);

export default FeeInfo;
